import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { BRAND, Meta, pages, resolveMeta } from "./pageList";

export { DEFAULT_DESCRIPTION } from "./pageList";

const privatePrefixes = ["/admin", "/cart", "/checkout", "/login", "/register", "/account", "/orders", "/forgot-password", "/reset-password", "/search"];
// Tab titles for private pages, which search engines are told not to index.
const privateTitles: Record<string, string> = {
  "/cart": "Your cart", "/checkout": "Checkout", "/login": "Sign in", "/register": "Create your account",
  "/account": "Your account", "/orders": "Your orders", "/forgot-password": "Reset your password",
  "/reset-password": "Choose a new password", "/search": "Search results",
};

function setTag(selector: string, create: () => HTMLElement, attr: string, value: string) {
  let el = document.head.querySelector<HTMLElement>(selector);
  if (!el) { el = create(); document.head.appendChild(el); }
  el.setAttribute(attr, value);
}
const metaName = (name: string, content: string) =>
  setTag(`meta[name="${name}"]`, () => { const m = document.createElement("meta"); m.setAttribute("name", name); return m; }, "content", content);
const metaProp = (property: string, content: string) =>
  setTag(`meta[property="${property}"]`, () => { const m = document.createElement("meta"); m.setAttribute("property", property); return m; }, "content", content);

export function applyPageMeta(pathname: string, meta: Meta) {
  const { title, description, url, image, robots } = resolveMeta(pathname, meta);
  document.title = title;
  metaName("description", description);
  metaName("robots", robots);
  metaProp("og:title", title);
  metaProp("og:description", description);
  metaProp("og:url", url);
  metaProp("og:image", image);
  metaName("twitter:title", title);
  metaName("twitter:description", description);
  setTag('link[rel="canonical"]', () => { const l = document.createElement("link"); l.setAttribute("rel", "canonical"); return l; }, "href", url);
}

/** Sets the title, description and canonical URL for the current route. */
export function usePageMeta() {
  const { pathname } = useLocation();
  useEffect(() => {
    const privatePrefix = privatePrefixes.find((p) => pathname === p || pathname.startsWith(p + "/"));
    const known = pages[pathname];
    if (known) applyPageMeta(pathname, known);
    else if (privatePrefix) applyPageMeta(pathname, { title: privateTitles[privatePrefix] || BRAND, noindex: true });
    // These pages set their own title once their data loads.
    else if (pathname.startsWith("/product/") || pathname.startsWith("/game-product-detail/") || pathname.startsWith("/shop/")) return;
    else applyPageMeta(pathname, { title: "Page not found", noindex: true });
  }, [pathname]);
}

/** Adds, replaces or (with null) removes a JSON-LD structured data block identified by `id`. */
export function setJsonLd(id: string, data: object | null) {
  const existing = document.head.querySelector(`script[type="application/ld+json"][data-seo="${id}"]`);
  if (!data) { existing?.remove(); return; }
  const el = existing || Object.assign(document.createElement("script"), { type: "application/ld+json" });
  el.setAttribute("data-seo", id);
  el.textContent = JSON.stringify(data).replace(/</g, "\\u003c");
  if (!existing) document.head.appendChild(el);
}

// Catalogue title and description helpers live in pageList.ts so the build
// script can use them too.
export { summarise, productTitle, machineTitle, categoryMeta, localDescription, productMeta, machineMeta } from "./pageList";
